import { useState } from 'react'
import { Button, Form, Input, Select, DatePicker, Tag, InputNumber, App } from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { quotationsApi, rfqApi, serviceMastersApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { Quotation } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const STATUS_COLORS: Record<string, string> = { OPEN: 'blue', SELECTED: 'green', REJECTED: 'red' }

export default function QuotationEntryPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['quotations', plantFilter],
    queryFn: () => quotationsApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: rfqs } = useQuery({ queryKey: ['rfqs-open'], queryFn: () => rfqApi.list({ status: 'OPEN' }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })
  const { data: services } = useQuery({ queryKey: ['service-masters'], queryFn: () => serviceMastersApi.list() })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const rfqOptions = rfqs?.map(r => ({ label: `${r.rfq_no} — ${r.description.slice(0, 40)}`, value: r.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []
  const serviceOptions = services?.map(s => ({ label: s.service_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        validity_date: values.validity_date ? dayjs(values.validity_date as string).format('YYYY-MM-DD') : undefined,
      }
      return quotationsApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['quotations'] }); message.success('Quotation created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const columns = [
    { title: 'Quot No', dataIndex: 'quot_no', key: 'quot_no', width: 120 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'RFQ', dataIndex: 'rfq_no', key: 'rfq_no', width: 120 },
    { title: 'Total Value', dataIndex: 'total_value', key: 'total_value', width: 120,
      render: (v: string | null) => v ? Number(v).toLocaleString('en-IN') : '—' },
    { title: 'Valid Till', dataIndex: 'validity_date', key: 'validity_date', width: 110 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Quotations"
        subtitle="Record supplier quotations against RFQs"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ lines: [{ description: '', quantity: 1, uom: 'PCS', rate: 0, tax_pct: 18 }] }); setDrawerOpen(true) }}>New Quotation</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Quotation"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
        width={800}
      >
        <Form.Item name="rfq_id" label="Against RFQ">
          <Select allowClear options={rfqOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="validity_date" label="Validity Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="payment_terms" label="Payment Terms">
          <Input />
        </Form.Item>
        <Form.Item name="delivery_terms" label="Delivery Terms">
          <Input />
        </Form.Item>

        <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 8 }}>Quotation Lines</div>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <div key={key} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'flex-start' }}>
                  <Form.Item name={[name, 'description']} style={{ flex: 2, marginBottom: 0 }} rules={[{ required: true }]}>
                    <Input placeholder="Description" />
                  </Form.Item>
                  <Form.Item name={[name, 'quantity']} style={{ width: 80, marginBottom: 0 }}>
                    <InputNumber placeholder="Qty" min={0.001} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'uom']} style={{ width: 70, marginBottom: 0 }}>
                    <Input placeholder="UOM" />
                  </Form.Item>
                  <Form.Item name={[name, 'rate']} style={{ width: 100, marginBottom: 0 }}>
                    <InputNumber placeholder="Rate" min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'tax_pct']} style={{ width: 80, marginBottom: 0 }}>
                    <InputNumber placeholder="GST%" min={0} max={28} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'service_id']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select allowClear placeholder="Service" options={serviceOptions} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: 8 }} />
                </div>
              ))}
              <Button type="dashed" onClick={() => add({ description: '', quantity: 1, uom: 'PCS', rate: 0, tax_pct: 18 })} icon={<PlusOutlined />} block>
                Add Line
              </Button>
            </>
          )}
        </Form.List>
      </FormDrawer>
    </div>
  )
}
